import { Controller } from "stimulus"

// #1612: de begeleide rondleiding na de onboarding.
//
// Zoekt de knop van deze stap (data-rondleiding="<doel>"), markeert hem met
// een spotlight en zet het venster ernaast. Staat de knop niet op de pagina,
// dan blijft het venster in het midden (op de telefoon als blad onderin) en
// verschijnt de knop "Ga naar ...".
//
// De knoppen in het venster zijn gewone formulieren. Deze controller verstuurt
// ze met fetch: hoort de volgende stap op een andere pagina, dan gaat Turbo
// daarheen; anders wisselt alleen het venster en blijft alles staan wat de
// gebruiker op de pagina invulde (niets gaat verloren bij sluiten).
export default class extends Controller {
  static targets = ["spot", "venster", "gaNaar"]
  static values = { doel: String, url: String, weergave: String }

  connect() {
    this.plaats = this.plaats.bind(this)
    window.addEventListener("resize", this.plaats)
    window.addEventListener("scroll", this.plaats, true)
    this.toetsen = this.toetsen.bind(this)
    document.addEventListener("keydown", this.toetsen)
    // Na het laden van de pagina (en na een Turbo-render) opnieuw zoeken
    requestAnimationFrame(() => this.plaats())
    this.later = setTimeout(() => this.plaats(), 400)
  }

  disconnect() {
    window.removeEventListener("resize", this.plaats)
    window.removeEventListener("scroll", this.plaats, true)
    document.removeEventListener("keydown", this.toetsen)
    clearTimeout(this.later)
  }

  doel() {
    if (!this.doelValue) return null
    const kandidaten = document.querySelectorAll(`[data-rondleiding~="${this.doelValue}"]`)
    return Array.from(kandidaten).find((el) => {
      if (this.element.contains(el)) return false
      const rect = el.getBoundingClientRect()
      return rect.width > 0 && rect.height > 0 && getComputedStyle(el).visibility !== "hidden"
    }) || null
  }

  smal() {
    return window.matchMedia("(max-width: 639px)").matches
  }

  plaats() {
    if (!this.hasVensterTarget) return
    const doel = this.doel()
    const venster = this.vensterTarget

    if (this.hasGaNaarTarget) this.gaNaarTarget.hidden = !!doel
    venster.classList.toggle("is-compact", !!doel && this.smal())
    if (!doel) {
      if (this.hasSpotTarget) this.spotTarget.hidden = true
      venster.classList.remove("is-geplaatst")
      venster.style.top = ""
      venster.style.left = ""
      return
    }

    if (!this.gescrold) {
      this.gescrold = true
      if (this.smal()) {
        // Het blad staat onderin: de knop moet in het bovenste deel van het scherm
        const boven = doel.getBoundingClientRect().top
        if (boven < 80 || boven > window.innerHeight * 0.3) window.scrollBy(0, boven - 96)
      } else {
        doel.scrollIntoView({ block: "center", inline: "nearest", behavior: "auto" })
      }
    }

    const rect = doel.getBoundingClientRect()
    const marge = 6
    if (this.hasSpotTarget) {
      const spot = this.spotTarget
      spot.hidden = false
      spot.style.top = `${rect.top - marge}px`
      spot.style.left = `${rect.left - marge}px`
      spot.style.width = `${rect.width + marge * 2}px`
      spot.style.height = `${rect.height + marge * 2}px`
    }

    // Op de telefoon staat het venster als blad onderin (CSS)
    if (this.smal()) {
      venster.classList.remove("is-geplaatst")
      return
    }

    venster.classList.add("is-geplaatst")
    const breedte = venster.offsetWidth
    const hoogte = venster.offsetHeight
    const ruimte = 16
    let top = rect.bottom + 14
    if (top + hoogte > window.innerHeight - ruimte) top = rect.top - hoogte - 14
    if (top < ruimte) top = Math.max(ruimte, window.innerHeight - hoogte - ruimte)
    let left = rect.left + rect.width / 2 - breedte / 2
    left = Math.min(Math.max(ruimte, left), window.innerWidth - breedte - ruimte)
    venster.style.top = `${top}px`
    venster.style.left = `${left}px`
  }

  toetsen(event) {
    // Escape sluit het venster (pauzeren), net als het kruisje
    if (event.key !== "Escape" || !this.hasVensterTarget) return
    const sluit = this.vensterTarget.querySelector(".rl-sluit")
    if (sluit) sluit.form.requestSubmit()
  }

  async verstuur(event) {
    event.preventDefault()
    const form = event.target.closest("form")
    const vraag = form.dataset.rlBevestig
    if (vraag && !window.confirm(vraag)) return

    const knop = form.querySelector("button")
    if (knop) knop.disabled = true
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const antwoord = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json", "X-CSRF-Token": token || "" },
        credentials: "same-origin"
      })
      const gegevens = await antwoord.json()
      const doelUrl = gegevens.url && new URL(gegevens.url, window.location.href)
      if (doelUrl && doelUrl.pathname + doelUrl.search !== window.location.pathname + window.location.search) {
        window.Turbo ? window.Turbo.visit(gegevens.url) : (window.location.href = gegevens.url)
        return
      }
      this.vervang(gegevens.html)
    } catch (fout) {
      // Zonder JSON: gewoon het formulier versturen
      form.submit()
    }
  }

  vervang(html) {
    if (!html) return
    const sjabloon = document.createElement("template")
    sjabloon.innerHTML = html.trim()
    const nieuw = sjabloon.content.firstElementChild
    if (!nieuw) return
    this.element.replaceWith(nieuw)
    nieuw.querySelector(".rl-venster")?.focus({ preventScroll: true })
  }
}
